import React, { ReactNode, useEffect, useState } from 'react';

import Button from './Button';
import apiCalls from '../PowerShadesAPIFunctions';
import { useQuoteIdAndDispatch } from '../Store/entities/quotes/hooks';
import { useDealerById, useDistributorById, useRepresentativeById } from '../Store/entities/hooks';
import { GetPaymentMethods2Response } from '../powershadesApiTypes';
import { useIsDealer, useIsDistributor, useIsRepresentative } from '../hooks';

export type CardAccount = {
	id: string;
	isAccount: boolean;
	lastFourDigits: string;
	status: string;
}

type NewCardFieldProps = {
	half: string;
	setPaymentType: (card: CardAccount) => void;
	quoteId: number;
	setIsActuallyAccount: (isAccount: boolean) => void;
	quoteType?: string;
	isActuallyAccount?: boolean;
};

const NewCardField = (props: NewCardFieldProps) => {
	const {
		half,
		setPaymentType,
		quoteId,
		setIsActuallyAccount,
		quoteType,
		isActuallyAccount,
	} = props;

	const [paymentMethods, setPayments] = useState<GetPaymentMethods2Response | null>(null);
	const [gettingPayments, setGettingPayments] = useState(false);
	const [cardBeingUsed, setCardBeingUsed] = useState<CardAccount | null>(null);
	const [zohoLink, setZohoLink] = useState<string | null>(null);

	const [willNeedRetainer, setWillNeedRetainer] = useState<null | boolean>(null);

	const { cards, accounts } = paymentMethods ?? {};

	const quote = useQuoteIdAndDispatch(quoteId);
	const dealer = useDealerById(quote?.dealer_id ?? 0);
	const distributor = useDistributorById(quote?.distributor_id ?? 0);
	const representative = useRepresentativeById(quote?.representative_id ?? 0);


	const useCard = (card: CardAccount) => {
		setCardBeingUsed(card);
	};

	const isRep = useIsRepresentative();
	const isDistributor = useIsDistributor();
	const isDealer = useIsDealer();

	useEffect(() => {
		if(isDealer) {
		apiCalls.getPaymentMethods2("Dealers", dealer?.id ?? 0).then((resp) => {
			const paymentItems = resp.data;

			setPayments(paymentItems);
		});
	} else if(isDistributor) {
		apiCalls.getPaymentMethods2("Distributors", distributor?.id ?? 0).then((resp) => {
			const paymentItems = resp.data;

			setPayments(paymentItems);
		}
	);
	} else if(isRep) {
		apiCalls.getPaymentMethods2("Representatives", representative?.id ?? 0).then((resp) => {
			const paymentItems = resp.data;

			setPayments(paymentItems);
		});
	}

	
	}, [])


	const openLink = (link: string) => {
		const newWindow = window.open(link, '_blank', 'noopener,noreferrer');
		if (newWindow) {
			newWindow.opener = null; // Prevents the new window from accessing the opener
		} else {
			// toast.error('Failed to open new window. Please allow pop-ups for this site.');
		}
		setZohoLink(link);
	}

	const addACard = () => {
		if (isDealer) {
			apiCalls.getAddCardLink("Dealers", dealer?.id ?? 0).then((resp) => {
				const { link } = resp.data;
				openLink(link);
			});
		} else if (isDistributor) {
			apiCalls.getAddCardLink("Distributors", distributor?.id ?? 0).then((resp) => {
				const { link } = resp.data;
				openLink(link);
			});
		} else if (isRep) {
			apiCalls.getAddCardLink("Representatives", representative?.id ?? 0).then((resp) => {
				const { link } = resp.data;
				openLink(link);
			});
		}



		setGettingPayments(true);

		const intervalObj: {
			gettingCard: boolean,
			oldCards: string,
			expire: number,
			count: number,
			id: any
		} = {
			gettingCard: false,
			oldCards: '',
			expire: 0,
			count: 0,
			id: null,
		};


		intervalObj.oldCards = JSON.stringify(paymentMethods);

		intervalObj.expire = 48;
		intervalObj.count = 0;
		intervalObj.gettingCard = true;

		intervalObj.id = setInterval(() => {
			if (!intervalObj.gettingCard) {
				clearInterval(intervalObj.id);
			}

			apiCalls.getPaymentMethods2("Dealers", quote?.dealer_id ?? 0).then((resp) => {
				if (intervalObj.expire <= intervalObj.count) {
					clearInterval(intervalObj.id);
					setGettingPayments(false);
					intervalObj.gettingCard = false;
				}

				const newCards = resp.data;

				if (JSON.stringify(newCards) != intervalObj.oldCards) {
					setPayments(newCards);
					setGettingPayments(false);
					clearInterval(intervalObj.id);
					intervalObj.gettingCard = false;
				}
				intervalObj.count += 1;
			});
		}, 5000);

		// Add Card state
		// Must contain an explanation
		// On return we add instructions and open a window
		// Current state must have a cancel button and a done button.
		// Open a new window with the information
	};

	const cardChosen = ({ id, isAccount, lastFourDigits, status }: CardAccount) => {
		if (!isAccount) {
			apiCalls.cardSelected(quoteId).then(() => {
				console.log('cardSelected');
			});
		} else {
			apiCalls.achSelected(quoteId).then(() => {
				console.log('achSelected');
			});
		}
		setPaymentType({
			id,
			isAccount,
			lastFourDigits,
			status: status
		});

		setIsActuallyAccount(isAccount);

		useCard({ id,lastFourDigits,isAccount,status });

		const retainerFunc = isAccount ? apiCalls.achRetainerNeeded : apiCalls.cardRetainerNeeded;

		retainerFunc(quoteId).then((resp) => {
			if (resp.status !== 200) {
				console.error(resp.data);
				throw new Error('Something went wrong checking if this order needs a retainer. Check console logs for more details.');
			} else {
				setWillNeedRetainer(resp.data.is_retainer_required);
			}
		});
	};

	const addCard = (
		<Button color={quoteType ?? 'green'} onClick={addACard}>
			Add Payment
		</Button>
	);

	let content: ReactNode = '.....Loading';

	if (!paymentMethods) {
	} else if (cardBeingUsed) {
		const hasPendingAccount = cardBeingUsed.status == 'pending';
		const isAccount = isActuallyAccount;

		const paymentTypeDisplay = isActuallyAccount ? 'Bank account' : 'Card';

		const moredisplay = isActuallyAccount ? 'account' : 'card';

		content = (
			<div>
				{`${paymentTypeDisplay} with the last four digits ${cardBeingUsed.lastFourDigits} will be used to pay for this order whenever the order is submitted. `}
				{willNeedRetainer ? (
					<div>
						<br />
						<div>
							{`This ${moredisplay} will be charged 50% of the invoice (${half}) because this order requires a retainer.`}
						</div>
						<br />
					</div>
				) : null}

				{hasPendingAccount ? (
					<div>
						<br />
						<div>
							This account is currently pending. Shades will not start production or
							be shipped until the verification process is complete.
						</div>
						<br />
					</div>
				) : null}
				{isAccount ? (
					<>If that is ok please press submit.</>
				) : (
					<>
						{`There will be a 3% credit card charge added onto the invoice. Paying via ACH will avoid this charge. Click submit to continue with card payment. `}
					</>
				)}
			</div>
		);
	} else if (gettingPayments) {
		content = (
			<div>
				<div>Waiting for a new payment method to be entered</div>
				<div>
					<Button color="gray" onClick={() => setGettingPayments(false)}>
						Cancel
					</Button>
				</div>
				{zohoLink ? (
					<div>
						<br />
						<a target="_blank" rel="noopener noreferrer" href={zohoLink}>
							If another window/tab doesn't open click here to add a card.
							{' '}
						</a>
					</div>
				) : null}
			</div>
		);
	} else if (!gettingPayments) {
		const cardRows = !cards
			? []
			: cards.map((c) => (
				<tr>
					<td>{c.last_four_digits}</td>
					<td>{c.card_type}</td>
					<td>{c.created_by_name}</td>
					<td>
						<Button
							color={quoteType ?? 'green'}
							disabled={c.is_expired}
							onClick={() => {
								cardChosen({
									id: c.card_id,
									isAccount: false,
									lastFourDigits: c.last_four_digits,
									status: c.status
								});
							}}
						>
							Use This Card
						</Button>
					</td>
				</tr>
			));

		const accountRows = !accounts
			? []
			: accounts.map((a) => (
				<tr>
					<td>{a.last_four_digits}</td>
					<td>{a.bank_name}</td>
					<td>{a.status}</td>
					<td>
						<Button
							color={quoteType ?? 'green'}
							disabled={a.status.toLocaleLowerCase().includes('failed')}
							onClick={() => {
								cardChosen(
									{
										id: a.account_id,
										isAccount: true,
										lastFourDigits: a.last_four_digits,
										status: a.status
									}
								);
							}}
						>
							Use This Account
						</Button>
					</td>
				</tr>
			));

		content = (
			<div>
				{cardRows.length == 0 ? null : (
					<table>
						<tbody>
							<tr>
								<th>Card Number</th>
								<th>Card Type</th>
								<th>Card Name</th>
								<th>Card Select</th>
							</tr>
							{cardRows}
						</tbody>
					</table>
				)}
				{accountRows.length == 0 ? null : (
					<table>
						<tbody>
							<tr>
								<th>Account Number</th>
								<th>Bank Name</th>
								<th>Account Status</th>
								<th>Account Select</th>
							</tr>
							{accountRows}
						</tbody>
					</table>
				)}
				{addCard}
			</div>
		);
	}

	return <div style={{ display: 'flex', flexDirection: 'row' }}>{content}</div>;
};

export default NewCardField;
